// Cursor paging for the submission list surfaces (Order Tracking, Submissions).
//
// WHY THIS EXISTS
// GET /api/submissions serves ONE page. Every caller used to ask for a fixed
// `limit` and render whatever came back as if it were the whole queue, so the
// moment a status outgrew that limit the surplus fundraisers stopped existing
// as far as the rep was concerned — silently, with no empty state, no banner
// and no error. Measured live 2026-08-25: 812 `ordered` docs against Order
// Tracking's limit of 500, and 453 `final_approved` against the Submissions
// tab's limit of 200. Roughly 580 live fundraisers were unreachable in the UI.
//
// So: follow nextCursor to the end, and carry the server's exact `total` so a
// surface can prove it is showing everything instead of assuming it.
//
// NOTE: lib/ordering.ts has fetchAllReorderSubmissions(), a narrower pager
// predating this one and hard-wired to the two Reorder statuses. Collapsing it
// into this file is a deliberate follow-up, not an oversight — that file is
// being edited concurrently and merging both at once would conflict.

import type { AnySub } from './ordering';

export const DEFAULT_PAGE_SIZE = 500;

// 40 x 500 = 20,000 rows. A backstop against an endless cursor loop, not an
// expected limit — `complete:false` says out loud when it is hit.
export const MAX_PAGES = 40;

export type DegradedSort = {
  reason: string;
  wantedSortField: string;
  indexUrl: string | null;
};

// Generic over the row type so a caller with a richer row (Submissions.tsx uses
// `Submission`) keeps its type. The runtime guard can only prove `submissions`
// is an array — element shape is asserted, exactly as the `useSWR<ListPayload>`
// annotations these calls replaced already did.
export type SubmissionPage<T = AnySub> = {
  submissions: T[];
  nextCursor: string | null;
  total?: number | null;
  sortedBy?: string;
  degraded?: DegradedSort;
};

export type LoadedSubmissions<T = AnySub> = {
  submissions: T[];
  /** Server-side row count for this filter at the time of the first page; null = unknown. */
  total: number | null;
  /** True only when the server said there were no more pages. */
  complete: boolean;
  sortedBy: string | null;
  degraded: DegradedSort | null;
};

/**
 * First-page URL. Kept as the SWR key so it still starts with
 * `/api/submissions?` — refreshSubmission() in lib/submission-ui.ts invalidates
 * by that string prefix, and an array key would silently stop refreshing these
 * surfaces after a mutation.
 */
export function submissionsPageUrl(
  opts: { status?: string; limit?: number } = {},
): string {
  const params = new URLSearchParams();
  params.set('limit', String(opts.limit ?? DEFAULT_PAGE_SIZE));
  if (opts.status) params.set('status', opts.status);
  return `/api/submissions?${params.toString()}`;
}

export function withCursor(url: string, cursor: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}cursor=${encodeURIComponent(cursor)}`;
}

function isSubmissionPage<T>(value: unknown): value is SubmissionPage<T> {
  if (!value || typeof value !== 'object') return false;
  const page = value as Record<string, unknown>;
  if (!Array.isArray(page.submissions)) return false;
  if (!Object.prototype.hasOwnProperty.call(page, 'nextCursor')) return false;
  return page.nextCursor === null
    || (typeof page.nextCursor === 'string' && page.nextCursor.length > 0);
}

/**
 * Read every page behind `firstPageUrl`.
 *
 * Throws rather than returning a short list: a partial answer that looks like a
 * complete one is the exact failure this module exists to remove, so a page
 * that fails mid-walk must surface as an error state, not as a shorter queue.
 */
export async function fetchAllSubmissions<T = AnySub>(
  firstPageUrl: string,
  fetchPage: (url: string) => Promise<unknown>,
  maxPages: number = MAX_PAGES,
): Promise<LoadedSubmissions<T>> {
  const submissions: T[] = [];
  const followed = new Set<string>();
  let cursor: string | null = null;
  let total: number | null = null;
  let sortedBy: string | null = null;
  let degraded: DegradedSort | null = null;

  for (let pageCount = 0; pageCount < maxPages; pageCount += 1) {
    const url = cursor ? withCursor(firstPageUrl, cursor) : firstPageUrl;
    const page = await fetchPage(url);
    if (!isSubmissionPage<T>(page)) throw new Error('Invalid page returned while loading submissions');
    submissions.push(...page.submissions);
    if (pageCount === 0) {
      total = typeof page.total === 'number' ? page.total : null;
      sortedBy = typeof page.sortedBy === 'string' ? page.sortedBy : null;
      degraded = page.degraded ?? null;
    }
    if (page.nextCursor === null) {
      return { submissions, total, complete: true, sortedBy, degraded };
    }
    if (followed.has(page.nextCursor)) {
      throw new Error('Repeated cursor returned while loading submissions');
    }
    followed.add(page.nextCursor);
    cursor = page.nextCursor;
  }

  return { submissions, total, complete: false, sortedBy, degraded };
}

/**
 * A sentence for the rep when the list is NOT provably whole — otherwise null.
 *
 * `complete` is the authoritative signal because it comes from nextCursor,
 * which is exact. `total` is a snapshot and can drift by a row or two while
 * paging, so it is only ever used to add detail to a warning, never to raise
 * one on its own.
 */
export function truncationNotice(loaded: LoadedSubmissions<unknown>): string | null {
  if (loaded.complete) return null;
  const shown = loaded.submissions.length;
  const of = loaded.total != null ? ` of ${loaded.total}` : '';
  return `Showing the first ${shown}${of} — too many rows to load at once. Narrow the filter or search to reach the rest.`;
}
